import { BrandFamilyLine } from "@/components/brand-family";
import { BrandLogo } from "@/components/brand-logo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, type ReactNode } from "react";
import { ArrowLeft } from "lucide-react";

import { breadcrumbSchema, seoHead } from "@/lib/seo";
import { JsonLd } from "@/components/seo/json-ld";

export const Route = createFileRoute("/privacy/")({
  ssr: true,
  head: () =>
    seoHead({
      title: "Privacy Policy",
      description:
        "How Bavimail collects, uses, and protects your data when you use our email infrastructure platform.",
      path: "/privacy/",
      // Legal-only boilerplate page; no search-traffic value. Excluding from
      // index frees crawl budget for the rest of the property (which has a
      // chronic ~50% "Discovered, not indexed" rate as of May 2026).
      noindex: true,
    }),
  component: PrivacyPage,
});

/* ─── Bullet list helper ───────────────────────────────── */

function Bl({ children }: { children: ReactNode }) {
  return <ul className="space-y-2.5 mt-3 ml-0.5">{children}</ul>;
}

function Li({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="w-1 h-1 rounded-full bg-[#f6821f] mt-[0.65em] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

/* ─── Section data ─────────────────────────────────────── */

const sections: { id: string; num: string; title: string; content: ReactNode }[] = [
  {
    id: "overview",
    num: "01",
    title: "Overview",
    content: (
      <>
        <p>
          Bavimail (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is an email
          infrastructure platform that provides email sending, receiving, tracking, and management
          services through APIs, SDKs, and a web dashboard.
        </p>
        <p>
          This Privacy Policy explains what information we collect, how we use it, and your choices
          regarding your data. It applies to all users of our website, dashboard, APIs, and related
          services.
        </p>
        <p>
          By using Bavimail, you agree to the collection and use of information as described in this
          policy. If you do not agree, please do not use our services.
        </p>
      </>
    ),
  },
  {
    id: "information-collected",
    num: "02",
    title: "Information We Collect",
    content: (
      <>
        <p>
          <strong>Account information.</strong> When you create an account, we collect your email
          address and password (stored as a cryptographic hash, never in plain text). If you sign in
          through a third-party provider (Google, GitHub, or Bavlio), we receive your name, email
          address, and profile identifier from that provider.
        </p>
        <p>
          <strong>Email data.</strong> When you send or receive email through Bavimail, we process
          message content, headers, metadata (sender, recipients, subject, timestamps), and
          attachments. This data is necessary to deliver, route, and store your email.
        </p>
        <p>
          <strong>Engagement data.</strong> When you enable open tracking or click tracking on
          outbound emails, we collect data about recipient interactions &mdash; specifically when a
          tracking pixel is loaded (opens) or a tracked link is clicked (clicks).
        </p>
        <p>
          <strong>Domain and DNS data.</strong> When you add a sending domain, we store the domain
          name and associated DNS records (SPF, DKIM, DMARC, MX, Return-Path) needed to authenticate
          and verify your domain.
        </p>
        <p>
          <strong>Usage and technical data.</strong> We automatically collect your IP address, user
          agent, browser language preferences, pages visited, API requests made, and feature usage
          patterns. This helps us operate, secure, and improve the platform.
        </p>
        <p>
          <strong>Payment information.</strong> Payment processing is handled entirely by Stripe. We
          do not store credit card numbers or bank account details. We receive transaction
          confirmations, invoice records, and subscription status from Stripe.
        </p>
        <p>
          <strong>Analytics data.</strong> We use PostHog to understand how people use the Bavimail
          dashboard. PostHog collects pageview data, session information, and interaction events.
          When you are logged in, we associate analytics data with your account to improve your
          experience.
        </p>
        <p>
          <strong>Error monitoring data.</strong> We use GlitchTip to capture client-side and
          server-side application errors, operational warnings, and limited diagnostic context
          needed to investigate reliability problems. This may include route paths, request timing,
          browser and device details, and account identifiers such as your user id or email when you
          are signed in. We configure GlitchTip to avoid storing sensitive secrets such as session
          cookies, authorization headers, and API keys.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use",
    num: "03",
    title: "How We Use Your Information",
    content: (
      <>
        <p>We use the information we collect to:</p>
        <Bl>
          <Li>Provide, operate, and maintain the Bavimail platform</Li>
          <Li>Process, deliver, route, and store email messages on your behalf</Li>
          <Li>Authenticate your identity and secure your account</Li>
          <Li>Track email engagement when you enable open or click tracking</Li>
          <Li>Process payments and manage your subscription</Li>
          <Li>Analyze usage patterns and improve the platform</Li>
          <Li>Monitor for abuse, spam, and policy violations</Li>
          <Li>Maintain suppression lists and process bounces and complaints</Li>
          <Li>Communicate with you about your account, billing, and service changes</Li>
          <Li>Comply with legal obligations</Li>
        </Bl>
        <p>
          We process your data based on our contractual obligation to provide the service you signed
          up for, our legitimate interest in improving and securing the platform, and your consent
          where applicable.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    num: "04",
    title: "Cookies & Tracking",
    content: (
      <>
        <p>
          <strong>Session cookies.</strong> We use cookies to authenticate your sessions. These are
          set by our authentication service and are essential for the platform to function. They
          persist for the duration of your session or longer if you choose &ldquo;remember me&rdquo;
          during sign-in.
        </p>
        <p>
          <strong>Analytics.</strong> We use PostHog for product analytics. PostHog may set cookies
          or use local storage to track sessions and distinguish unique visitors. It also records
          sessions on our website, with what you type into forms hidden. You can opt out of
          analytics tracking by using browser privacy settings or extensions that block PostHog.
        </p>
        <p>
          <strong>Google Analytics.</strong> We use Google Analytics to count visits to our website
          and see where they come from. It reports to two Google Analytics accounts:
          Bavimail&rsquo;s, and one for Bavlio Inc., the company behind Bavimail, which also
          measures bavlio.com and rosefall.ai so that a visit across these sites counts once. Google
          Analytics sets cookies. You can opt out with{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f6821f] hover:underline"
          >
            Google&rsquo;s opt-out browser add-on
          </a>
          .
        </p>
        <p>
          <strong>Advertising.</strong> Google Ads, the Meta Pixel and the Reddit Pixel measure
          visits that come from our ads on Google, Meta and Reddit, and let those companies show our
          ads to people who have visited our website. They set cookies and send each visit to
          Google, Meta and Reddit. To stop ads based on your visits, use your{" "}
          <a
            href="https://myadcenter.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f6821f] hover:underline"
          >
            Google ad settings
          </a>
          ,{" "}
          <a
            href="https://www.facebook.com/adpreferences"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f6821f] hover:underline"
          >
            Meta ad preferences
          </a>{" "}
          and{" "}
          <a
            href="https://www.reddit.com/settings/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f6821f] hover:underline"
          >
            Reddit privacy settings
          </a>
          .
        </p>
        <p>
          <strong>Cloudflare Web Analytics.</strong> Cloudflare counts page loads on our website and
          measures how fast they load. It sets no cookies.
        </p>
        <p>
          <strong>Error monitoring.</strong> GlitchTip may receive technical error and request
          metadata when the application encounters a failure. We use this solely for debugging,
          reliability monitoring, and incident response.
        </p>
        <p>
          <strong>Email open tracking.</strong> When you enable open tracking on outbound emails, a
          small transparent pixel is included in the message. When a recipient loads this image, we
          record the open event. Recipients can disable image loading in their email client to
          prevent this.
        </p>
        <p>
          <strong>Email click tracking.</strong> When you enable click tracking, links in your
          outbound emails are routed through our servers. When a recipient clicks a tracked link, we
          record the click and redirect them to the original URL.
        </p>
        <p>
          <strong>External resources.</strong> Our website loads fonts from Google Fonts. This
          results in your browser making requests to Google&rsquo;s servers, which is subject to{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f6821f] hover:underline"
          >
            Google&rsquo;s privacy policy
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    num: "05",
    title: "Third-Party Services",
    content: (
      <>
        <p>We rely on the following third-party services to operate the platform:</p>
        <Bl>
          <Li>
            <strong>PostHog</strong> &mdash; Product analytics. Data processed in the United States.
          </Li>
          <Li>
            <strong>GlitchTip</strong> &mdash; Application error monitoring and diagnostic log
            storage.
          </Li>
          <Li>
            <strong>Stripe</strong> &mdash; Payment processing. Data processed in the United States.
          </Li>
          <Li>
            <strong>Amazon Web Services (AWS)</strong> &mdash; Email delivery infrastructure and
            hosting.
          </Li>
          <Li>
            <strong>Google</strong> &mdash; OAuth authentication and web font delivery.
          </Li>
          <Li>
            <strong>GitHub</strong> &mdash; OAuth authentication.
          </Li>
          <Li>
            <strong>Railway</strong> &mdash; Application hosting and deployment.
          </Li>
        </Bl>
        <p>
          Each third-party service processes data according to their own privacy policies. We share
          only the minimum data necessary for each service to perform its function.
        </p>
      </>
    ),
  },
  {
    id: "data-sharing",
    num: "06",
    title: "Data Sharing",
    content: (
      <>
        <p>
          <strong>Service providers.</strong> We share data with the third-party services listed
          above, only as needed to operate the platform. These providers are contractually obligated
          to protect your data.
        </p>
        <p>
          <strong>Legal requirements.</strong> We may disclose your information if required by law,
          subpoena, court order, or governmental regulation, or when we believe disclosure is
          necessary to protect our rights, your safety, or the safety of others.
        </p>
        <p>
          <strong>Business transfers.</strong> If Bavimail is involved in a merger, acquisition, or
          sale of assets, your information may be transferred as part of that transaction. We will
          notify you of any such change.
        </p>
        <p>
          <strong>No sale of personal data.</strong> We do not sell, rent, or trade your personal
          information to third parties for their marketing purposes.
        </p>
      </>
    ),
  },
  {
    id: "data-retention",
    num: "07",
    title: "Data Retention",
    content: (
      <>
        <p>
          <strong>Account data.</strong> We retain your account information for as long as your
          account is active. If you delete your account, we remove your personal data within 30
          days, except where retention is required by law.
        </p>
        <p>
          <strong>Email data.</strong> Emails and attachments are retained according to your
          plan&rsquo;s retention period &mdash; 7 days on Free, 30 days on Pro, and 90 days on
          Startup. Enterprise customers may negotiate custom retention periods. After the retention
          period, email data is permanently deleted.
        </p>
        <p>
          <strong>Analytics data.</strong> Aggregated analytics data (without personal identifiers)
          may be retained indefinitely to improve the platform.
        </p>
        <p>
          <strong>Error monitoring data.</strong> Error events and diagnostic logs are retained
          according to our operational retention settings and are deleted when they are no longer
          needed for debugging, incident response, or reliability analysis.
        </p>
        <p>
          <strong>Payment records.</strong> Transaction and invoice records are retained as required
          by tax and accounting regulations, typically for 7 years.
        </p>
        <p>
          <strong>Technical logs.</strong> Server logs containing IP addresses and request data are
          retained for up to 90 days for security and debugging purposes.
        </p>
      </>
    ),
  },
  {
    id: "data-security",
    num: "08",
    title: "Data Security",
    content: (
      <>
        <p>We implement technical and organizational measures to protect your data:</p>
        <Bl>
          <Li>All data in transit is encrypted using TLS</Li>
          <Li>Stored data is encrypted at rest</Li>
          <Li>Passwords are hashed using industry-standard algorithms</Li>
          <Li>Two-factor authentication (TOTP) is available for all accounts</Li>
          <Li>API keys are scoped and rate-limited</Li>
          <Li>Access to production systems is restricted and audited</Li>
        </Bl>
        <p>
          No system is perfectly secure. While we take reasonable precautions, we cannot guarantee
          absolute security. You are responsible for keeping your account credentials and API keys
          confidential.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    num: "09",
    title: "Your Rights",
    content: (
      <>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <Bl>
          <Li>
            <strong>Access</strong> &mdash; Request a copy of the personal data we hold about you
          </Li>
          <Li>
            <strong>Correction</strong> &mdash; Update or correct inaccurate information
          </Li>
          <Li>
            <strong>Deletion</strong> &mdash; Request deletion of your account and personal data
          </Li>
          <Li>
            <strong>Export</strong> &mdash; Receive your data in a portable format
          </Li>
          <Li>
            <strong>Objection</strong> &mdash; Object to certain types of data processing
          </Li>
          <Li>
            <strong>Restriction</strong> &mdash; Request that we limit how we use your data
          </Li>
        </Bl>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:support@bavimail.com" className="text-[#f6821f] hover:underline">
            support@bavimail.com
          </a>
          . We will respond within 30 days.
        </p>
        <p>
          You can also manage your data directly through the dashboard: update your profile, delete
          your account, or configure email tracking and retention preferences.
        </p>
      </>
    ),
  },
  {
    id: "children",
    num: "10",
    title: "Children's Privacy",
    content: (
      <>
        <p>
          Bavimail is designed for professional and business use. Our services are not directed at
          individuals under the age of 16. We do not knowingly collect personal information from
          children. If we learn that we have collected data from a child under 16, we will delete
          that information promptly.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    num: "11",
    title: "Changes to This Policy",
    content: (
      <>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, or legal requirements. When we make material changes, we will notify you by
          email or by posting a notice in the dashboard at least 30 days before the changes take
          effect.
        </p>
        <p>
          The &ldquo;Last updated&rdquo; date at the top of this page indicates when the policy was
          most recently revised. Your continued use of Bavimail after changes become effective
          constitutes your acceptance of the revised policy.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    num: "12",
    title: "Contact Us",
    content: (
      <>
        <p>
          If you have questions about this Privacy Policy or how we handle your data, contact us:
        </p>
        <div className="mt-4 px-5 py-4 border border-dashed border-[#2a2a2e] bg-[#0a0a0c] inline-block">
          <div className="text-[#d0d0d0] font-semibold font-hb mb-1">Bavimail</div>
          <a href="mailto:support@bavimail.com" className="text-[#f6821f] hover:underline">
            support@bavimail.com
          </a>
        </div>
      </>
    ),
  },
];

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

function PrivacyPage() {
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollPct(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Privacy Policy", path: "/privacy/" },
        ])}
      />
      <style>{`
        .font-hb { font-family: 'Manrope', system-ui, sans-serif; }
        .font-mono-hb { font-family: 'Chivo Mono', monospace; }
        .bg-grid-hb {
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .hb-accent-bar {
          background: linear-gradient(90deg, #f6821f, #ff6633, #f6821f);
          background-size: 200% 100%;
        }
      `}</style>

      <div className="font-hb min-h-screen bg-[#0d0d0f] text-[#d0d0d0] relative selection:bg-[#f6821f] selection:text-black flex flex-col">
        <div className="fixed inset-0 bg-grid-hb pointer-events-none" />
        <div
          className="h-[3px] fixed top-0 left-0 z-[60] hb-accent-bar"
          style={{
            width: `${scrollPct * 100}%`,
            transition: "width 0.15s ease-out",
          }}
        />

        {/* ── Header ──────────────────────────────────── */}
        <header className="sticky top-[3px] z-50 border-b border-[#1a1a1e] bg-[#0d0d0f]/80 backdrop-blur-md">
          <div className="max-w-4xl mx-auto flex items-center justify-between px-6 md:px-8 py-4">
            <Link
              to="/"
              aria-label="Bavimail home"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <BrandLogo className="h-6 w-auto" />
            </Link>
            <Link
              to="/"
              className="group flex items-center gap-2 font-mono-hb text-[11px] text-[#7a7a7a] hover:text-[#f6821f] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to home
            </Link>
          </div>
        </header>

        {/* ── Content ─────────────────────────────────── */}
        <main className="relative z-10 flex-grow max-w-4xl mx-auto w-full px-6 md:px-8 py-12 md:py-16 lg:py-20">
          {/* Title block */}
          <div className="mb-14">
            <div className="font-mono-hb text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a] mb-4">
              Legal
            </div>
            <h1 className="font-hb font-extrabold text-3xl md:text-[2.75rem] text-white tracking-tight leading-tight mb-4">
              Privacy Policy
            </h1>
            <div className="flex flex-wrap items-center gap-3 font-mono-hb text-[11px] text-[#7a7a7a]">
              <span>Effective March 15, 2026</span>
              <span className="w-px h-3 bg-[#2a2a2e]" />
              <span>Last updated October 20, 2026</span>
            </div>
            <p className="mt-6 font-mono-hb text-[13px] text-[#8a8a8a] leading-[1.85] max-w-2xl">
              This Privacy Policy describes how Bavimail collects, uses, and protects your
              information when you use our email infrastructure platform, APIs, dashboard, and
              website.
            </p>
          </div>

          {/* Table of contents */}
          <nav className="relative border border-dashed border-[#2a2a2e] p-5 md:p-6 mb-14 bg-[#111114]">
            <div className="absolute -top-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -top-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -bottom-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -bottom-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="font-mono-hb text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a] mb-4">
              Contents
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2.5 font-mono-hb text-[12px] text-[#8a8a8a] hover:text-[#f6821f] transition-colors py-1"
                >
                  <span className="text-[#f6821f]/40 text-[11px]">{s.num}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </div>
          </nav>

          {/* Sections */}
          <div>
            {sections.map((section, i) => (
              <section
                key={section.id}
                id={section.id}
                className={i > 0 ? "border-t border-dashed border-[#2a2a2e] pt-10 mt-10" : ""}
                style={{ scrollMarginTop: "5rem" }}
              >
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-mono-hb text-[11px] text-[#f6821f]">/{section.num}</span>
                  <h2 className="font-hb font-bold text-[17px] md:text-lg text-white">
                    {section.title}
                  </h2>
                </div>
                <div className="font-mono-hb text-[13px] text-[#8a8a8a] leading-[1.85] space-y-4 [&_strong]:text-[#d0d0d0] [&_strong]:font-semibold max-w-[680px]">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </main>

        {/* ── Footer ──────────────────────────────────── */}
        <footer className="relative z-10 border-t border-[#1a1a1e] bg-[#0a0a0c] py-8 px-6 md:px-8">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-mono-hb text-[11px] text-[#6b6b6b]">
                &copy; {new Date().getFullYear()} Bavimail. All rights reserved.
              </span>
              <BrandFamilyLine
                className="font-mono-hb text-[11px] text-[#6b6b6b]"
                linkClassName="underline underline-offset-2 hover:text-[#f6821f]"
              />
            </div>
            <div className="flex items-center gap-4 font-mono-hb text-[11px] text-[#6b6b6b]">
              <span className="text-[#f6821f]">Privacy</span>
              <span className="w-px h-3 bg-[#1a1a1e]" />
              <Link to="/terms" className="hover:text-[#f6821f] transition-colors">
                Terms
              </Link>
              <span className="w-px h-3 bg-[#1a1a1e]" />
              <Link to="/" className="hover:text-[#f6821f] transition-colors">
                Home
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
